import { CARCASS_MM } from "./carcass";
import { resolveInternalItemY } from "./internalLayout";
import { cabinetBaseHeightMm } from "./installation";
import { cabinetInteriorFloorMm } from "./cabinetDrawers";
import { usableItemPieces } from "./compartments";
import { NOMINAL_WIDTH_FITTINGS } from "./wardrobeFittings";
import { clamp, snap } from "./project";
import type {
  FillingPresetType,
  InternalItem,
  WardrobeModule
} from "../types";

export const FILLING_PRESETS: Record<
  FillingPresetType,
  { name: string; description: string; minimumHeight: number }
> = {
  doubleHang: {
    name: "Короткая одежда",
    description: "Две штанги и разделительная полка",
    minimumHeight: 2300
  },
  longHang: {
    name: "Длинная одежда",
    description: "Высокая штанга и свободное место снизу",
    minimumHeight: 1900
  },
  shelvesDrawers: {
    name: "Полки и ящики",
    description: "Два ящика и полки для сложенных вещей",
    minimumHeight: 900
  },
  shoesAccessories: {
    name: "Обувь и аксессуары",
    description: "Обувница, корзина и полки",
    minimumHeight: 1250
  },
  mixed: {
    name: "Комбинированное",
    description: "Штанга, верхняя полка и два ящика",
    minimumHeight: 1800
  },
  empty: {
    name: "Пустая секция",
    description: "Удалить всё внутреннее наполнение",
    minimumHeight: 0
  }
};

type ItemDraft = Omit<InternalItem, "id">;

const item = (
  type: InternalItem["type"],
  y: number,
  patch: Partial<ItemDraft> = {}
): ItemDraft => ({ type, y: snap(y), ...patch });

const shelfLevels = (start: number, end: number, gap: number) => {
  const levels: number[] = [];
  for (let y = start; y <= end; y += gap) levels.push(snap(y));
  return levels;
};

const presetDrafts = (
  module: WardrobeModule,
  preset: FillingPresetType
): ItemDraft[] => {
  const height = module.height;
  const topShelf = snap(clamp(height - 150, 260, height - 82));

  if (preset === "empty") return [];

  if (preset === "doubleHang") {
    const middleShelf = snap(clamp(height * 0.46, 980, height - 1060));
    const lowerRail = snap(Math.max(992, middleShelf - 96));
    const upperRail = snap(Math.min(topShelf - 150, middleShelf + 960));
    return [
      item("shelf", middleShelf),
      item("shelf", topShelf),
      item("rail", lowerRail, { clothes: "shirts" }),
      item("rail", upperRail, { clothes: "shirts" })
    ];
  }

  if (preset === "longHang") {
    const bottomShelf = snap(176);
    const rail = snap(topShelf - 192);
    return [
      item("shelf", bottomShelf),
      item("shelf", topShelf),
      item("rail", rail, { clothes: "longDress" })
    ];
  }

  if (preset === "shelvesDrawers") {
    // Ящики не ниже 250 мм — правило цеха; расстановку по высоте добивает resolveInternalItemY.
    const drawers = height >= 1150
      ? [
          item("drawer", 352, { height: 250 }),
          item("drawer", 640, { height: 250 })
        ]
      : [item("drawer", 352, { height: 250 })];
    const shelfStart = drawers.length === 2 ? 704 : 416;
    const shelves = shelfLevels(shelfStart, height - 116, 360).map((y) =>
      item("shelf", y)
    );
    if (!shelves.length || Math.abs(shelves.at(-1)!.y - topShelf) > 180) {
      shelves.push(item("shelf", topShelf));
    }
    return [...drawers, ...shelves];
  }

  if (preset === "shoesAccessories") {
    const baseItems: ItemDraft[] = [
      item("shoeRack", 160),
      item("basket", 768, { height: 160 }),
      item("shelf", 896)
    ];
    const shelves = shelfLevels(1248, height - 116, 352).map((y) =>
      item("shelf", y)
    );
    if (!shelves.length || Math.abs(shelves.at(-1)!.y - topShelf) > 180) {
      shelves.push(item("shelf", topShelf));
    }
    return [...baseItems, ...shelves];
  }

  const upperRail = snap(topShelf - 192);
  return [
    item("drawer", 352, { height: 250 }),
    item("drawer", 640, { height: 250 }),
    item("shelf", topShelf),
    item("rail", upperRail, { clothes: "jackets" })
  ];
};

const placeDrafts = (
  module: WardrobeModule,
  drafts: ItemDraft[],
  idFactory: () => string
) => {
  const working: WardrobeModule = { ...module, items: [] };
  const structural = drafts.filter((draft) => draft.type !== "rail");
  const rails = drafts.filter((draft) => draft.type === "rail");

  [...structural, ...rails].forEach((draft) => {
    const next: InternalItem = { ...draft, id: idFactory() };
    working.items.push(next);
    next.y = resolveInternalItemY(working, next.id, next.y);
  });
  return working.items;
};

export const fillingPresetFits = (
  module: WardrobeModule,
  preset: FillingPresetType
) =>
  module.type !== "shelving" &&
  module.type !== "corner45" &&
  module.height >= FILLING_PRESETS[preset].minimumHeight;

export const buildFillingPreset = (
  module: WardrobeModule,
  preset: FillingPresetType,
  idFactory: () => string
) =>
  fillingPresetFits(module, preset)
    ? placeDrafts(module, presetDrafts(module, preset).filter((draft) =>
        // Над наружными ящиками уже есть неподвижная полка: нижние полки
        // заготовки, оказавшиеся на ней или вплотную, не нужны.
        !(draft.type === "shelf" && draft.y < cabinetInteriorFloorMm(module) + 60))
        // Корзины — только под пенал 600–900 мм (31.10): в широком пенале вместо корзины полка.
        .map((draft) => NOMINAL_WIDTH_FITTINGS.has(draft.type) &&
          !usableItemPieces(module, { ...draft, id: "probe" }).length
          ? { type: "shelf" as const, y: draft.y } : draft), idFactory)
    : module.items;

export const copyFillingLayout = (
  source: WardrobeModule,
  target: WardrobeModule,
  idFactory: () => string
) => {
  const sourceBottom =
    source.type === "mezzanine"
      ? CARCASS_MM
      : Math.max(80, cabinetBaseHeightMm(source) + CARCASS_MM);
  const targetBottom =
    target.type === "mezzanine"
      ? CARCASS_MM
      : Math.max(80, cabinetBaseHeightMm(target) + CARCASS_MM);
  const usableSourceHeight = Math.max(1, source.height - sourceBottom - 80);
  const usableTargetHeight = Math.max(1, target.height - targetBottom - 80);
  const heightRatio = usableTargetHeight / usableSourceHeight;
  const sourceInnerWidth = Math.max(1, source.width - 132);
  const targetInnerWidth = Math.max(1, target.width - 132);
  const widthRatio = targetInnerWidth / sourceInnerWidth;

  const drafts = source.items.map<ItemDraft>((sourceItem) => {
    const y = targetBottom + (sourceItem.y - sourceBottom) * heightRatio;
    if (sourceItem.type !== "divider") {
      return { ...sourceItem, y: snap(y) };
    }
    return {
      ...sourceItem,
      y: Math.round(y * 2) / 2,
      x: snap((sourceItem.x ?? 0) * widthRatio),
      height: Math.max(
        320,
        Math.min(
          target.height - 120,
          Math.round((sourceItem.height ?? 1400) * heightRatio)
        )
      )
    };
  });

  return placeDrafts(target, drafts, idFactory);
};
